import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import Chart from "react-apexcharts";

/**
 * Shared analytics pieces for the Marketing Dashboard and the campaign
 * detail page — both read GET /api/admin/advertisement/analytics/overview,
 * so they render its numbers the same way.
 */

export const formatNumber = (n) =>
  new Intl.NumberFormat("en-IN").format(Math.round(Number(n) || 0));
export const formatPercent = (n) => `${(Number(n) || 0).toFixed(2)}%`;
export const formatInr = (n) =>
  `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n) || 0)}`;

const istToday = () =>
  new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);
const shiftDays = (dateKey, days) =>
  new Date(Date.parse(`${dateKey}T00:00:00Z`) + days * 864e5)
    .toISOString()
    .slice(0, 10);

/** Default range: the last 30 IST days, inclusive. */
export const defaultRange = () => {
  const to = istToday();
  return { from: shiftDays(to, -29), to };
};

export const RANGE_PRESETS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];
export const rangeForPreset = (days) => {
  const to = istToday();
  return { from: shiftDays(to, -(days - 1)), to };
};

const Kpi = ({ label, value, hint }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

/** Impressions, reach, clicks, CTR, conversions, spend. */
export const KpiRow = ({ totals = {}, reachPartial }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 2,
      mb: 2,
    }}
  >
    <Kpi label="Impressions" value={formatNumber(totals.impressions)} />
    <Kpi
      label="Unique reach"
      value={formatNumber(totals.reach)}
      hint={
        reachPartial
          ? "Partial — raw events are kept 180 days"
          : "Distinct devices that saw an ad"
      }
    />
    <Kpi label="Clicks" value={formatNumber(totals.clicks)} />
    <Kpi label="CTR" value={formatPercent(totals.ctr)} />
    <Kpi
      label="Conversions"
      value={formatNumber(totals.conversions)}
      hint={`${formatPercent(totals.conversionRate)} of clicks`}
    />
    <Kpi label="Spend" value={formatInr(totals.spend)} hint="CPM / CPC campaigns" />
  </Box>
);

/** Daily impressions (left axis) and clicks (right axis) — clicks get their own scale so they don't flatten to zero. */
export const TrendChart = ({ daily = [], height = 300 }) => {
  const options = {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { width: [2, 2], curve: "smooth" },
    fill: { opacity: [0.2, 1] },
    dataLabels: { enabled: false },
    xaxis: {
      categories: daily.map((d) => d.date.slice(5)),
      tickAmount: Math.min(10, daily.length),
    },
    yaxis: [
      { title: { text: "Impressions" }, labels: { formatter: (v) => formatNumber(v) } },
      { opposite: true, title: { text: "Clicks" }, labels: { formatter: (v) => formatNumber(v) } },
    ],
    legend: { position: "top" },
    tooltip: { shared: true, intersect: false },
    colors: ["#3f51b5", "#00a76f"],
  };
  const series = [
    { name: "Impressions", type: "area", data: daily.map((d) => d.impressions) },
    { name: "Clicks", type: "line", data: daily.map((d) => d.clicks) },
  ];
  return <Chart options={options} series={series} type="line" height={height} />;
};
